/**
 * AuthIllustration — animated hero panel for AuthShell.
 *
 * Replaces the earlier five-node floating graph (which sat on top of
 * the marketing copy) with a composition that respects the text
 * corridor and communicates what FTL actually does:
 *
 *   1. Aurora gradient mesh — atmosphere, behind everything.
 *   2. Subtle starfield grid — depth, never crosses text zone.
 *   3. Vertical SDLC pipeline on the RIGHT EDGE (x > 600px in the
 *      800-wide viewBox) showing Objective → User Story → Task →
 *      Test → Baseline, each as a small labeled card with a
 *      connecting rail and tiny packets flowing downward.
 *
 * The text column in AuthShell sits at ``p-10 xl:p-14`` with
 * ``max-w-lg`` (512px), so its readable zone is roughly x=40..560
 * in our 800-wide viewBox. Everything the user reads lives there.
 * Everything we draw here lives above, below, or to the right of
 * that zone — never on top of it.
 *
 * All motion respects ``useReducedMotion``.
 */

import { m, useReducedMotion } from "framer-motion";

// SDLC pipeline stages — the Felix lifecycle in one glance.
const PIPELINE = [
  { id: "objective", letter: "O", label: "Objective", cy: 140 },
  { id: "user_story", letter: "S", label: "User Story", cy: 260 },
  { id: "component", letter: "C", label: "Component", cy: 380 },
  { id: "task", letter: "T", label: "Task", cy: 500 },
  { id: "test", letter: "V", label: "Test", cy: 620 },
  { id: "baseline", letter: "B", label: "Baseline", cy: 740 },
] as const;

const PIPELINE_X = 700; // right-edge column, well outside the text zone
const PIPELINE_RADIUS = 18;

export function AuthIllustration() {
  const reducedMotion = useReducedMotion();

  const breathe = reducedMotion
    ? undefined
    : {
        opacity: [0.85, 1, 0.85],
        transition: {
          duration: 4.5,
          repeat: Infinity,
          ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
        },
      };

  return (
    <>
      {/* Aurora gradient mesh */}
      <div className="absolute inset-0 opacity-90">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 15% 20%, rgba(59,122,255,0.28) 0%, transparent 65%), " +
              "radial-gradient(ellipse 70% 80% at 85% 75%, rgba(139,92,246,0.24) 0%, transparent 65%), " +
              "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(6,214,224,0.18) 0%, transparent 65%)",
          }}
        />
      </div>

      {/* Drifting noise for texture */}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.8'/></svg>\")",
        }}
      />

      {/* Starfield grid — faint dots that imply a plane of data */}
      <svg
        className="absolute inset-0 h-full w-full pointer-events-none"
        viewBox="0 0 800 900"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <pattern id="starfield" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.18)" />
          </pattern>
          <linearGradient id="pipeline-rail" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(125,250,255,0.9)" />
            <stop offset="50%" stopColor="rgba(59,122,255,0.7)" />
            <stop offset="100%" stopColor="rgba(139,92,246,0.8)" />
          </linearGradient>
          <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
          </filter>
          <linearGradient id="packet-trail" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.9)" />
          </linearGradient>
        </defs>

        {/* Starfield — only paint the columns outside the text zone so
            dots never land behind copy. Text zone ≈ x=40..560, so we
            paint x=0..40 and x=580..800. */}
        <rect x="0" y="0" width="40" height="900" fill="url(#starfield)" opacity="0.8" />
        <rect x="580" y="0" width="220" height="900" fill="url(#starfield)" opacity="0.8" />

        {/* Pipeline rail — thin vertical line connecting the SDLC stages */}
        <line
          x1={PIPELINE_X}
          y1={PIPELINE[0].cy}
          x2={PIPELINE_X}
          y2={PIPELINE[PIPELINE.length - 1].cy}
          stroke="url(#pipeline-rail)"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.55"
        />

        {/* Data packets — small dots that travel down the rail to
            convey "work flowing through the pipeline". Two packets
            offset in time so the rail never sits empty. */}
        {!reducedMotion &&
          [0, 3].map((delay) => (
            <m.circle
              key={delay}
              cx={PIPELINE_X}
              r="3"
              fill="url(#packet-trail)"
              initial={{ cy: PIPELINE[0].cy, opacity: 0 }}
              animate={{
                cy: [PIPELINE[0].cy, PIPELINE[PIPELINE.length - 1].cy],
                opacity: [0, 1, 1, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                delay,
                ease: "linear",
                times: [0, 0.08, 0.92, 1],
              }}
            />
          ))}

        {/* SDLC stage nodes — rounded card + letter glyph + label */}
        {PIPELINE.map((stage, idx) => (
          <m.g
            key={stage.id}
            initial={reducedMotion ? undefined : { opacity: 0, y: 12 }}
            animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.3 + idx * 0.12,
              ease: [0.2, 0.8, 0.2, 1],
            }}
          >
            {/* Soft halo behind node */}
            <m.circle
              cx={PIPELINE_X}
              cy={stage.cy}
              r={PIPELINE_RADIUS + 6}
              fill="rgba(255,255,255,0.12)"
              filter="url(#node-glow)"
              animate={breathe}
            />
            <circle
              cx={PIPELINE_X}
              cy={stage.cy}
              r={PIPELINE_RADIUS}
              fill="rgba(255,255,255,0.96)"
            />
            <text
              x={PIPELINE_X}
              y={stage.cy + 5}
              textAnchor="middle"
              fontSize="14"
              fontWeight="700"
              fill="#1d5ff5"
            >
              {stage.letter}
            </text>
            {/* Stage label to the LEFT of the node — sits in the gap
                between the text column (max-w-lg ≈ x<570) and the
                pipeline circles (x=700). Text-anchored to the end
                so labels grow leftward without crossing the circles
                or getting clipped at the viewBox right edge. */}
            <text
              x={PIPELINE_X - PIPELINE_RADIUS - 12}
              y={stage.cy + 4}
              textAnchor="end"
              fontSize="13"
              fontWeight="600"
              fill="rgba(255,255,255,0.85)"
              letterSpacing="0.02em"
            >
              {stage.label}
            </text>
          </m.g>
        ))}

        {/* Top + bottom pipeline caps — subtle arrowheads to imply
            direction of flow (up → down, latest work descends). */}
        <m.path
          d={`M ${PIPELINE_X - 6} ${PIPELINE[0].cy - 34} L ${PIPELINE_X} ${PIPELINE[0].cy - 22} L ${PIPELINE_X + 6} ${PIPELINE[0].cy - 34}`}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reducedMotion ? undefined : { opacity: 0 }}
          animate={reducedMotion ? undefined : { opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        />
        <m.path
          d={`M ${PIPELINE_X - 6} ${PIPELINE[PIPELINE.length - 1].cy + 34} L ${PIPELINE_X} ${PIPELINE[PIPELINE.length - 1].cy + 22} L ${PIPELINE_X + 6} ${PIPELINE[PIPELINE.length - 1].cy + 34}`}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reducedMotion ? undefined : { opacity: 0 }}
          animate={reducedMotion ? undefined : { opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        />
      </svg>
    </>
  );
}
